import { useQuery } from '@apollo/client'
import { Circle, DoNotDisturb } from '@mui/icons-material'
import { IconButton, Stack, Tooltip } from '@mui/material'
import { type ReactNode } from 'react'
import { GetRosterColors } from 'recruiter/graphql/rosters.gql'
import { useCandidateSqlSearch } from 'recruiter/hooks'
import { getPxAsRem } from 'shared/utils'

function ColorDot({
    label,
    hexCode,
    isActive,
    onClick,
    icon,
}: {
    label: string
    hexCode: string
    isActive: boolean
    onClick: () => void
    icon?: ReactNode
}) {
    return (
        <Tooltip title={label} arrow>
            <IconButton
                size="small"
                onClick={onClick}
                sx={{
                    padding: getPxAsRem(1),
                    borderRadius: '50%',
                    outline: isActive ? '1px solid' : '1px solid transparent',
                    outlineColor: isActive ? hexCode : 'transparent',
                    outlineOffset: 0,
                    marginRight: getPxAsRem(1),
                }}
            >
                {icon ?? (
                    <Circle sx={{ color: hexCode, fontSize: getPxAsRem(12) }} />
                )}
            </IconButton>
        </Tooltip>
    )
}

export function ToolbarColorFilter() {
    const { tableState, setTableState } = useCandidateSqlSearch()
    const { data } = useQuery(GetRosterColors)

    const colors = data?.rosterColors
        ? [...data.rosterColors].sort((a, b) => a.order - b.order)
        : []
    const activeColorNames = tableState.filters.colorNames

    if (colors.length === 0) return null

    const toggle = (colorName: string) => {
        const next = activeColorNames.includes(colorName)
            ? activeColorNames.filter((n) => n !== colorName)
            : [...activeColorNames, colorName]
        setTableState({ filters: { ...tableState.filters, colorNames: next } })
    }

    return (
        <Stack direction="row" alignItems="center" gap={getPxAsRem(2)}>
            {colors.map((color) => {
                const isActive = activeColorNames.includes(color.value)
                return (
                    <ColorDot
                        key={color.id}
                        label={color.value}
                        hexCode={color.hexCode}
                        isActive={isActive}
                        onClick={() => toggle(color.value)}
                    />
                )
            })}
            <ColorDot
                label="No Color"
                hexCode="#BDBDBD"
                isActive={activeColorNames.includes('None')}
                onClick={() => toggle('None')}
                icon={
                    <DoNotDisturb
                        sx={{ fontSize: getPxAsRem(12), color: '#BDBDBD' }}
                    />
                }
            />
        </Stack>
    )
}
